import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { PlanLimitConfigs, BillingCycleConfig, Plan } from '../types.ts';

export const JPD_DEFAULT_BILLING_CYCLE = 'MONTHLY';
export const JPD_DEFAULT_UNIT_COUNT = 10;

//  - At `unitCount >= 100`, the calculator switches to Enterprise pricing
//    (replaces Premium pricing in the Premium card slot — see
//    `isUnitCountAboveMaxThreshold` in selectors.ts).
//  - Enterprise becomes the *recommended/selected* card only at
//    `unitCount >= 250` (driven by `recommendationLimit: 250` on the
//    Enterprise plan limit config below).
export const JPD_MIN_ENTERPRISE_USER_COUNT = 100;

/**
 * The complete list of JPD offering SKUs known to code, INCLUDING the
 * Enterprise SKU added for GWP-10907. Whether the Enterprise SKU is
 * actually rendered is decided at runtime by `useIsJpdEnterpriseSkuEnabled`
 * — do NOT use this map directly to drive UI; consumers should go through
 * the hooks below so the GWP-10907 feature flag gates everything from a
 * single source of truth.
 */
export const jpdOfferingSkus = {
	free: 'jira-servicedesk-free', // jpd-free shares the same sku as jsm-free
	standard: 'jira-product-discovery-standard',
	premium: 'jira-product-discovery-premium-sku',
	enterprise: 'jira-product-discovery-enterprise',
} as const;

export const jpdBillingCycleConfig: BillingCycleConfig = {
	monthly: true,
	annual: true,
};

const JPD_FREE_PLAN_LIMIT_CONFIG: PlanLimitConfigs = {
	planName: 'Free',
	offeringSku: jpdOfferingSkus.free,
	recommendationLimit: 0,
	signupLimit: 20_000,
};

const JPD_STANDARD_PLAN_LIMIT_CONFIG: PlanLimitConfigs = {
	planName: 'Standard',
	offeringSku: jpdOfferingSkus.standard,
	recommendationLimit: 3,
	signupLimit: 20_000,
};

const JPD_PREMIUM_PLAN_LIMIT_CONFIG: PlanLimitConfigs = {
	planName: 'Premium',
	offeringSku: jpdOfferingSkus.premium,
	recommendationLimit: 50,
	signupLimit: 20_000,
};

const JPD_ENTERPRISE_PLAN_LIMIT_CONFIG: PlanLimitConfigs = {
	planName: 'Enterprise',
	offeringSku: jpdOfferingSkus.enterprise,
	recommendationLimit: 250,
	signupLimit: 20_000,
	minUnitCount: JPD_MIN_ENTERPRISE_USER_COUNT,
};

/**
 * Single source of truth for whether the JPD Enterprise SKU is "live"
 * for the current request. Drives:
 * - `useJpdPlanLimitConfigs` (which the calculator/resilience-guard uses
 *   to decide whether the Enterprise pricing card is rendered)
 * - `useJpdPlanTitlesAllowList` (which the compare-features pricing
 *   table uses to decide whether the Enterprise column is rendered)
 * - whether the JPD `highlightedFeaturesContainer` (i.e. the bottom
 *   benefits section) is rendered on the cards
 *
 * GWP-10907.
 */
export const useIsJpdEnterpriseSkuEnabled = (): boolean => {
	return checkFeatureFlagGate(featureFlags.gwp10907JpdEnterpriseSku);
};

/**
 * Non-hook equivalent of `useJpdPlanLimitConfigs`. Safe to call outside
 * React (e.g. in server-side enhancers). The Enterprise entry is included
 * only when the GWP-10907 feature flag is on.
 */
export const jpdPlanLimitConfigs = (): PlanLimitConfigs[] => {
	const isEnterpriseSkuEnabled = checkFeatureFlagGate(featureFlags.gwp10907JpdEnterpriseSku);
	const base: PlanLimitConfigs[] = [
		JPD_FREE_PLAN_LIMIT_CONFIG,
		JPD_STANDARD_PLAN_LIMIT_CONFIG,
		JPD_PREMIUM_PLAN_LIMIT_CONFIG,
	];
	return isEnterpriseSkuEnabled ? [...base, JPD_ENTERPRISE_PLAN_LIMIT_CONFIG] : base;
};

/**
 * Non-hook equivalent of `useJpdOfferingSkusArray`. Safe to call outside
 * React (e.g. in server-side enhancers). Includes the Enterprise SKU only
 * when the GWP-10907 feature flag is on.
 */
export const jpdOfferingSkusArray = (): string[] =>
	jpdPlanLimitConfigs().map((config) => config.offeringSku);

/**
 * Active JPD `PlanLimitConfigs[]` for the current request. The Enterprise
 * entry is included only when `useIsJpdEnterpriseSkuEnabled` returns
 * `true`. This is the single source of truth that drives both the pricing
 * calculator (via the resilience guard which filters out unknown SKUs)
 * AND the compare-features table (via `useJpdPlanTitlesAllowList`).
 */
export const useJpdPlanLimitConfigs = (): PlanLimitConfigs[] => {
	return jpdPlanLimitConfigs();
};

/**
 * Active JPD offering SKUs as a flat array for the current request.
 * Includes the Enterprise SKU only when the FF is on.
 */
export const useJpdOfferingSkusArray = (): string[] => {
	return useJpdPlanLimitConfigs().map((config) => config.offeringSku);
};

/**
 * Active JPD `planTitleExternal` strings for the current request.
 * Used by `UniformPricingTable` to filter compare-features columns
 * to the same allow-list that drives the pricing cards.
 */
export const useJpdPlanTitlesAllowList = (): string[] => {
	return useJpdPlanLimitConfigs().map((config) => config.planName);
};

/**
 * Whether the JPD pricing cards should render their bottom
 * `highlightedFeaturesContainer` section. Gated behind the same
 * GWP-10907 feature flag so content authors can stage the new
 * highlighted-features content in CMS without it being shown in
 * production until the launch.
 */
export const useShouldShowJpdHighlightedFeatures = (): boolean => {
	return useIsJpdEnterpriseSkuEnabled();
};

/**
 * Pure helper that returns a NEW array of `Plan` objects with the
 * `highlightedFeaturesContainer` and `highlightedFeaturesTitle` fields
 * stripped from each plan. Used to suppress the per-card "highlighted
 * features" bottom section without mutating the original CMS-provided
 * data.
 *
 * Stripping these fields causes `getFeaturesList(...)` (in price-
 * calculator.tsx) to return `undefined`, which in turn:
 *   1. Sets `numSections={1}` on the `<PricingCards>` grid, and
 *   2. Causes each individual `<PricingCard>` to render only its top
 *      section (no benefits / bottom section).
 *
 * Both effects MUST be in lock-step, which is why sanitization happens
 * BEFORE `numSections` is computed in `price-calculator.tsx` — see
 * `useShouldStripJpdHighlightedFeatures` below.
 */
export const stripJpdHighlightedFeatures = (plans: Plan[]): Plan[] =>
	plans.map(
		(plan) =>
			({
				...plan,
				fields: {
					...plan.fields,
					highlightedFeaturesContainer: undefined,
					highlightedFeaturesTitle: undefined,
				},
			}) as unknown as Plan,
	);

/**
 * Hook that decides whether the JPD-specific "strip highlighted features"
 * sanitizer should run for a given page. Centralises the JPD-specific
 * `urlSlug === "product-discovery"` check + GWP-10907 FF check so the
 * shared `UniformPricingCalculator` no longer has to know about either.
 *
 * Returns `true` ONLY when:
 *   - the consumer page IS the JPD pricing page
 *     (`urlSlug === "product-discovery"`), AND
 *   - the GWP-10907 highlighted-features FF is OFF (i.e.
 *     `useShouldShowJpdHighlightedFeatures()` returns `false`).
 *
 * Returns `false` for every other product (Loom, JSM, Confluence, Jira,
 * etc.) so this hook is safe to call unconditionally from the shared
 * calculator.
 */
export const useShouldStripJpdHighlightedFeatures = (urlSlug?: string): boolean => {
	const shouldShow = useShouldShowJpdHighlightedFeatures();
	return urlSlug === 'product-discovery' && !shouldShow;
};
